import { type FC, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { VIDEO_PLAYBACK_PROCESSING, type VideoPlaybackResolver, type VideoStatusSubscriber } from '@/types';

import { tr } from './translations';

const VideoJsPlayer = lazy(() =>
  import('@/videoJsPlayer/VideoJsPlayer').then((module) => ({ default: module.VideoJsPlayer })),
);

// Renew the capability halfway through its lifetime so a long watch never stalls
// on an expired URL. Falls back to 30 minutes when the lifetime cannot be read.
const FALLBACK_RENEW_AFTER_MS = 30 * 60 * 1000;

// While the video is still processing, ask again on this schedule, repeating the last delay.
const PROCESSING_POLL_DELAYS_MS = [3_000, 5_000, 10_000, 15_000];

// The URL carries the gateway's JWT as its `token` param. Only the lifetime
// (exp - iat, both server clock) is used, so browser clock skew does not matter.
const renewAfterMs = (url: string): number => {
  try {
    const token = new URL(url).searchParams.get('token') ?? '';
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { iat, exp } = JSON.parse(atob(payload));
    const lifetimeMs = (exp - iat) * 1000;
    return lifetimeMs > 0 ? lifetimeMs / 2 : FALLBACK_RENEW_AFTER_MS;
  } catch {
    return FALLBACK_RENEW_AFTER_MS;
  }
};

export interface VideoPlayerProps {
  videoId: string;
  resolvePlayback: VideoPlaybackResolver;
  className?: string;
  /** Poster image shown before playback starts. Defaults to the authorization's poster. */
  poster?: string;
  /** Start playing as soon as the source is authorized (best effort). */
  autoPlay?: boolean;
  /** Status updates for a processing video; a ready update re-authorizes without waiting for the next poll. */
  subscribeToStatus?: VideoStatusSubscriber;
  translations?: Record<string, string>;
}

type PlayerState = 'loading' | 'processing' | 'playing' | 'error';

export const VideoPlayer: FC<VideoPlayerProps> = ({
  videoId,
  resolvePlayback,
  className,
  poster,
  autoPlay,
  subscribeToStatus,
  translations,
}) => {
  const mediaRef = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [authorizedPoster, setAuthorizedPoster] = useState<string | undefined>();
  const [state, setState] = useState<PlayerState>('loading');
  const [authorizationAttempt, setAuthorizationAttempt] = useState(0);
  const recovery = useRef<'idle' | 'pending' | 'swapped'>('idle');
  const sourceVideoId = useRef<string | null>(null);
  const casting = useRef(false);
  const renewAfterCast = useRef<(() => void) | null>(null);

  // A playback error usually means an expired signed URL, so re-authorize and
  // swap in a fresh one. Errors the old source raises while that is pending stay
  // hidden. A fresh URL that fails before loading any media is a real failure,
  // so the player's error dialog takes over instead of looping.
  const handlePlaybackError = () => {
    if (recovery.current === 'swapped') return false;
    if (recovery.current === 'idle') {
      recovery.current = 'pending';
      setAuthorizationAttempt((attempt) => attempt + 1);
    }
    return true;
  };

  // A cast session reloads the receiver whenever this changes, so it changes only with the video.
  const resolveCastSrc = useCallback(
    () =>
      resolvePlayback(videoId, { cast: true })
        .then((authorization) =>
          authorization && authorization !== VIDEO_PLAYBACK_PROCESSING ? authorization.url : null,
        )
        .catch(() => null),
    [resolvePlayback, videoId],
  );

  // A new tab URL reloads a cast from the tab's paused position, so a renewal waits for the cast to end.
  const handleCastChange = useCallback((connected: boolean) => {
    casting.current = connected;
    if (connected) return;
    renewAfterCast.current?.();
    renewAfterCast.current = null;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: A recovery restarts authorization from scratch.
  useEffect(() => {
    if (!videoId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let latestLoad = 0;
    let processingPolls = 0;
    let unsubscribe: (() => void) | undefined;

    const renew = () => {
      if (casting.current) renewAfterCast.current = renew;
      else void load();
    };

    const load = async () => {
      const thisLoad = ++latestLoad;
      // Retry authorization once before giving up; a fresh URL request often
      // clears a transient failure.
      const authorization =
        (await resolvePlayback(videoId).catch(() => null)) ?? (await resolvePlayback(videoId).catch(() => null));
      // Drop results that resolve after cleanup, a videoId change, or a newer load.
      if (cancelled || thisLoad !== latestLoad) return;
      if (authorization === VIDEO_PLAYBACK_PROCESSING) {
        setState('processing');
        unsubscribe ??= subscribeToStatus?.(videoId, (update) => {
          if (update.type !== 'status' || update.status !== 'ready') return;
          clearTimeout(timer);
          void load();
        });
        const delay = PROCESSING_POLL_DELAYS_MS[Math.min(processingPolls++, PROCESSING_POLL_DELAYS_MS.length - 1)];
        timer = setTimeout(() => void load(), delay);
        return;
      }
      unsubscribe?.();
      unsubscribe = undefined;
      if (!authorization) {
        setState('error');
        return;
      }

      const media = mediaRef.current;
      if (media && sourceVideoId.current === videoId) {
        // Swap the source underneath a live element, then put the viewer back
        // where they were once the new manifest is ready. Autoplay only applies
        // to the first source, so a renewal never resumes a paused video.
        const position = media.currentTime;
        const wasPlaying = !media.paused;
        media.autoplay = false;
        media.addEventListener(
          'loadedmetadata',
          () => {
            if (position > 0) media.currentTime = position;
            if (wasPlaying) void media.play();
          },
          { once: true },
        );
      }

      if (recovery.current === 'pending') recovery.current = 'swapped';
      sourceVideoId.current = videoId;
      setAuthorizedPoster(authorization.posterUrl ?? undefined);
      setSrc(authorization.url);
      setState('playing');
      timer = setTimeout(renew, renewAfterMs(authorization.url));
    };

    void load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      renewAfterCast.current = null;
      unsubscribe?.();
    };
  }, [videoId, resolvePlayback, subscribeToStatus, authorizationAttempt]);

  if (state === 'processing') {
    return (
      <div className={className} data-video-id={videoId} data-player-state={state}>
        <div
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl bg-black text-white"
          style={{ aspectRatio: '16 / 9' }}
        >
          <span
            className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
          <p role="status" className="text-sm">
            {tr(translations, 'video.status_processing')}
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={className} data-video-id={videoId} data-player-state={state}>
        <p role="status" className="text-center text-sm text-black/50">
          {tr(translations, 'video.unavailable')}
        </p>
      </div>
    );
  }

  return (
    <div className={className} data-video-id={videoId} data-player-state={state}>
      {src && (
        <Suspense fallback={<div className="w-full rounded-xl bg-black" style={{ aspectRatio: '16 / 9' }} />}>
          <VideoJsPlayer
            ref={mediaRef}
            src={src}
            poster={poster ?? authorizedPoster}
            autoPlay={autoPlay}
            preload="metadata"
            onPlaybackError={handlePlaybackError}
            resolveCastSrc={resolveCastSrc}
            onCastChange={handleCastChange}
            onLoadedData={() => {
              recovery.current = 'idle';
            }}
            className="w-full overflow-hidden rounded-xl bg-black"
            style={{ aspectRatio: '16 / 9' }}
          >
            <track kind="captions" />
          </VideoJsPlayer>
        </Suspense>
      )}
    </div>
  );
};
